import { useEffect, useState } from "react";
import PageName from "../components/PageName";
import { useSelector } from "react-redux";
import HeaderLogo from "../components/HeaderLogo";
import Loading from "../components/Loading";
import { CropImage } from "../components/CropImage";
import { useForm } from "react-hook-form";
import { fetchAuthPost, reduceRepeatElement } from "../functions/functions";
import useStaticData from "../hooks/useStaticData";
import api from "../routes/api";
import { useContentModal } from "../hooks/useContentModal";

export default function OrganizationSettings() {
  const [isLoading, setIsLoading] = useState(true);
  const { msg } = useSelector((state) => state.language);
  const [org, setOrg] = useState(null);
  const formHook = useForm();
  const {
    register,
    setValue,
    reset,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = formHook;

  const initFetch = async () => {
    const resOrg = await fetchAuthPost(api.setting.readOrganization);
    setOrg(resOrg);
  };

  const onReset = (data) => {
    reset({ ...data, _temp: { isEdit: false, isView: true } });
  };

  const onCancel = () => {
    onReset(org.data);
    setValue("_temp.isEdit", false);
    setValue("_temp.isView", true);
    setValue("organization.postal_code", org.data.organization.postal_code);
    setValue("company.postal_code", org.data.company.postal_code);
    setValue(
      "organization.organization_logo",
      org.data.organization.organization_logo
    );
    trigger("organization.organization_logo");
  };

  const onEdit = () => {
    setValue("_temp.isEdit", true);
    setValue("_temp.isView", false);
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      let { organization: org, company: comp, guest_setting: gs } = data;
      if (data.same_as_company_address) {
        org = {
          ...org,
          address: comp.address,
          id_country: comp.id_country,
          id_province: comp.id_province,
          id_district: comp.id_district,
          id_subdistrict: comp.id_subdistrict,
          postal_code: comp.postal_code,
        };
      }

      const { id_organization, ...reOrg } = org;
      const { id_company, ...reComp } = comp;
      const { id_guest_setting, ...reGs } = gs;

      const payloadUpdate = {
        data: {
          organization: {
            ...reOrg,
            id_organization,
          },
          company: {
            ...reComp,
            id_company,
          },
          guest_setting: {
            ...reGs,
            id_guest_setting,
          },
        },
      };
      await fetchAuthPost(api.setting.updateOrganization, payloadUpdate);
      const resOrg = await fetchAuthPost(api.setting.readOrganization);
      if (resOrg.status === "error") throw resOrg;
      setOrg(resOrg);
      onReset(resOrg.data);
      setValue(
        "organization.postal_code",
        resOrg.data.organization.postal_code
      );
      setValue("company.postal_code", resOrg.data.company.postal_code);
    } catch (error) {
      console.error(error);
      alert(msg.alert, error.msg || msg.try_again);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    initFetch();
  }, []);
  useEffect(() => {
    if (org?.status === "success") {
      onReset(org.data);
      setValue("organization.postal_code", org.data.organization.postal_code);
      setValue("company.postal_code", org.data.company.postal_code);
      setIsLoading(false);
    }
  }, [org]);
  if (isLoading) return <Loading />;
  return (
    <div className="page_organization_settings">
      <HeaderLogo />
      <PageName titleName={msg.organization_settings} />
      <div className="section_form">
        <input type="hidden" {...register("_temp.isView")} />
        <input type="hidden" {...register("_temp.isEdit")} />
        <Organization formHook={formHook} />
        <Company formHook={formHook} />
        <GuestSetting
          formHook={formHook}
          onCancel={onCancel}
          onSave={onSubmit}
          onEdit={onEdit}
        />
      </div>
    </div>
  );
}

const Organization = ({ formHook }) => {
  const { msg, language } = useSelector((state) => state.language);
  const { setContent, setTitle, handleOpen, handleClose, ContentModal } =
    useContentModal();
  const { staticData } = useStaticData();
  const { allProvinceDistrictSubdistrictList: allAddress, countryList } =
    staticData;
  const countryOption = countryList.map((el) => ({
    value: el.id_country,
    label_en: el.label_en,
    label_th: el.label_th,
  }));
  const provinceOption = reduceRepeatElement(allAddress, "id_province").map(
    (el) => ({
      value: el.id_province,
      label_en: el.label_en_province,
      label_th: el.label_th_province,
    })
  );

  const {
    register,
    setValue,
    trigger,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = formHook;
  const [isSameAsCompanyAddress, setIsSameAsCompanyAddress] = useState(false);

  const _temp = watch("_temp");
  const is_same_as_company_address = watch(
    "organization.same_as_company_address"
  );
  const organization_id_province = watch("organization.id_province");
  const organization_id_district = watch("organization.id_district");
  const organization_id_subdistrict = watch("organization.id_subdistrict");
  const organization_postal_code = watch("organization.postal_code");
  const organization_organization_logo = watch(
    "organization.organization_logo"
  );
  const organization_organization_logo_name = watch(
    "organization.organization_logo_name"
  );

  const company_id_country = watch("company.id_country");
  const company_address = watch("company.address");
  const company_id_province = watch("company.id_province");
  const company_id_district = watch("company.id_district");
  const company_id_subdistrict = watch("company.id_subdistrict");
  const company_postal_code = watch("company.postal_code");

  const onOrganizationPostalCode = () => {
    const currentOrganizationPostalCode = organization_postal_code;
    if (
      organization_id_province &&
      organization_id_district &&
      organization_id_subdistrict
    ) {
      const found = allAddress.find(
        (el) =>
          +el.id_province === +organization_id_province &&
          +el.id_district === +organization_id_district &&
          +el.id_subdistrict === +organization_id_subdistrict
      );
      if (found && !currentOrganizationPostalCode) {
        setValue("organization.postal_code", found.postal_code);
      }
    }
  };

  const onSubmitLogo = (srcLogo) => {
    setValue("organization.organization_logo", srcLogo?.src);
    if (
      Boolean(srcLogo?.src) === true &&
      Boolean(srcLogo?.name) === false &&
      Boolean(organization_organization_logo_name) === true
    ) {
      setValue(
        "organization.organization_logo_name",
        organization_organization_logo_name || ""
      );
    } else if (Boolean(srcLogo?.src) === false) {
      setValue("organization.organization_logo_name", "");
    } else if (
      Boolean(srcLogo?.src) === true &&
      Boolean(srcLogo?.name) === true
    ) {
      setValue("organization.organization_logo_name", srcLogo?.name || "");
    }
    trigger("organization.organization_logo");
  };

  useEffect(() => {
    onOrganizationPostalCode();
  }, [
    organization_id_province,
    organization_id_district,
    organization_id_subdistrict,
  ]);

  useEffect(() => {
    if (organization_organization_logo) {
      handleClose();
    }
  }, [organization_organization_logo]);

  useEffect(() => {
    if (is_same_as_company_address) {
      setValue("organization.id_country", company_id_country);
      setValue("organization.address", company_address);
      setValue("organization.id_province", company_id_province);
      setValue("organization.id_district", company_id_district);
      setValue("organization.id_subdistrict", company_id_subdistrict);
      setValue("organization.postal_code", company_postal_code);
    }
  }, [
    is_same_as_company_address,
    company_id_country,
    company_address,
    company_id_province,
    company_id_district,
    company_id_subdistrict,
    company_postal_code,
  ]);

  return (
    <div className="organization">
      <h2>{msg.organization_information}</h2>
      <section>
        <div className="_input_text_select">
          <label>{`${msg.organization_name} * (${msg.thai})`}</label>
          <input
            placeholder={msg.organization_name}
            {...register("organization.organization_name_thai", {
              required: true,
            })}
            className={
              errors.organization?.organization_name_thai ? "_data_invalid" : ""
            }
            disabled={!_temp.isEdit}
          />
        </div>
        <div className="_input_text_select">
          <label>{`${msg.organization_name} (${msg.english})`}</label>
          <input
            placeholder={msg.organization_name}
            {...register("organization.organization_name_english")}
            disabled={!_temp.isEdit}
          />
        </div>
        <div className="_input_text_select">
          <label>{`${msg.address} *`}</label>
          <input
            placeholder={msg.address}
            {...register("organization.address", {
              required: !isSameAsCompanyAddress,
            })}
            className={errors.organization?.address ? "_data_invalid" : ""}
            disabled={!_temp.isEdit || isSameAsCompanyAddress}
          />
          <span>
            <input
              onClick={(e) => setIsSameAsCompanyAddress(e.target.checked)}
              type="checkbox"
              {...register("organization.same_as_company_address")}
              disabled={!_temp.isEdit}
            />
            <label>{msg.same_as_company_address}</label>
          </span>
        </div>
        <div className="select_address_organization_block">
          <div className="_input_text_select">
            <label>{`${msg.country} *`}</label>
            <select
              {...register("organization.id_country", {
                required: !isSameAsCompanyAddress,
              })}
              className={errors.organization?.id_country ? "_data_invalid" : ""}
              disabled={!_temp.isEdit || isSameAsCompanyAddress || true}
            >
              <option value="" label={msg.country} />
              {(countryOption || []).map((el) => (
                <option
                  key={+el.value}
                  value={+el.value || ""}
                  label={language === "en" ? el.label_en : el.label_th}
                />
              ))}
            </select>
          </div>
          <div className="_input_text_select">
            <label>{`${msg.province} *`}</label>
            <select
              {...register("organization.id_province", {
                required: !isSameAsCompanyAddress,
                onChange: () => {
                  setValue("organization.id_district", "");
                  setValue("organization.id_subdistrict", "");
                  setValue("organization.postal_code", "");
                },
              })}
              className={
                errors.organization?.id_province ? "_data_invalid" : ""
              }
              disabled={!_temp.isEdit || isSameAsCompanyAddress}
            >
              <option value="" label={msg.province} />
              {(provinceOption || []).map((el) => (
                <option
                  key={+el.value}
                  value={+el.value || ""}
                  label={language === "en" ? el.label_en : el.label_th}
                />
              ))}
            </select>
          </div>
          <div className="_input_text_select">
            <label>{`${msg.district} *`}</label>
            <select
              {...register("organization.id_district", {
                required: !isSameAsCompanyAddress,
                onChange: () => {
                  setValue("organization.id_subdistrict", "");
                  setValue("organization.postal_code", "");
                },
              })}
              className={
                errors.organization?.id_district ? "_data_invalid" : ""
              }
              disabled={!_temp.isEdit || isSameAsCompanyAddress}
            >
              <option value="" label={msg.district} />
              {(
                reduceRepeatElement(allAddress, "id_district")
                  .filter((el) => +el.id_province === +organization_id_province)
                  .map((el) => ({
                    value: el.id_district,
                    label_en: el.label_en_district,
                    label_th: el.label_th_district,
                  })) || []
              ).map((el) => (
                <option
                  key={+el.value}
                  value={+el.value}
                  label={language === "en" ? el.label_en : el.label_th}
                />
              ))}
            </select>
          </div>
          <div className="_input_text_select">
            <label>{`${msg.subdistrict} *`}</label>
            <select
              {...register("organization.id_subdistrict", {
                required: !isSameAsCompanyAddress,
                onChange: () => {
                  setValue("organization.postal_code", "");
                  onOrganizationPostalCode();
                },
              })}
              className={
                errors.organization?.id_subdistrict ? "_data_invalid" : ""
              }
              disabled={!_temp.isEdit || isSameAsCompanyAddress}
            >
              <option value="" label={msg.subdistrict} />
              {(
                reduceRepeatElement(
                  allAddress.filter(
                    (el) =>
                      +el.id_province === +organization_id_province &&
                      +el.id_district === +organization_id_district
                  ),
                  "id_subdistrict"
                ).map((el) => ({
                  value: el.id_subdistrict,
                  label_en: el.label_en_subdistrict,
                  label_th: el.label_th_subdistrict,
                })) || []
              ).map((el) => (
                <option
                  key={+el.value}
                  value={+el.value}
                  label={language === "en" ? el.label_en : el.label_th}
                />
              ))}
            </select>
          </div>
        </div>
        <div className="_input_text_select">
          <label>{`${msg.postal_code} *`}</label>
          <input
            {...register("organization.postal_code", {
              required: !isSameAsCompanyAddress,
            })}
            className={errors.organization?.postal_code ? "_data_invalid" : ""}
            placeholder={msg.postal_code}
            disabled={!_temp.isEdit || isSameAsCompanyAddress}
          />
        </div>
        <div className="_input_text_select">
          <label>{`${msg.organization_logo} *`}</label>
          <button
            onClick={() => {
              if (!_temp.isEdit) return;
              setTitle(msg.upload_logo);
              setContent(
                <div style={{ padding: 20 }}>
                  <CropImage
                    setSrc={onSubmitLogo}
                    existingImage={organization_organization_logo}
                  />
                </div>
              );
              handleOpen();
            }}
            className={
              "upload_file " +
              (errors.organization?.organization_logo ? "_data_invalid" : "")
            }
          >
            <div
              style={{
                backgroundColor:
                  organization_organization_logo && "var(--secondary)",
                color: organization_organization_logo && "var(--bright)",
              }}
            >
              {msg.upload_file}
            </div>
            <div>
              {(organization_organization_logo_name.length > 43
                ? `${organization_organization_logo_name.slice(0, 40)}...`
                : organization_organization_logo_name) ||
                msg.upload_your_logo_here}
            </div>
          </button>
          <input
            {...register("organization.organization_logo", { required: true })}
            type="hidden"
          />
        </div>
      </section>
      {ContentModal}
    </div>
  );
};

const Company = ({ formHook }) => {
  const { msg, language } = useSelector((state) => state.language);
  const { staticData } = useStaticData();
  const { allProvinceDistrictSubdistrictList: allAddress, countryList } =
    staticData;
  const countryOption = countryList.map((el) => ({
    value: el.id_country,
    label_en: el.label_en,
    label_th: el.label_th,
  }));
  const provinceOption = reduceRepeatElement(allAddress, "id_province").map(
    (el) => ({
      value: el.id_province,
      label_en: el.label_en_province,
      label_th: el.label_th_province,
    })
  );
  const {
    register,
    setValue,
    watch,
    getValues,
    trigger,
    handleSubmit,
    formState: { errors },
  } = formHook;

  const _temp = watch("_temp");
  const is_head_office = watch("company.head_office");
  const company_id_province = watch("company.id_province");
  const company_id_district = watch("company.id_district");
  const company_id_subdistrict = watch("company.id_subdistrict");
  const company_postal_code = watch("company.postal_code");

  const onCompanyPostalCode = () => {
    const currentCompanyPostalCode = company_postal_code;
    if (company_id_province && company_id_district && company_id_subdistrict) {
      const found = allAddress.find(
        (el) =>
          +el.id_province === +company_id_province &&
          +el.id_district === +company_id_district &&
          +el.id_subdistrict === +company_id_subdistrict
      );
      if (found && !currentCompanyPostalCode) {
        setValue("company.postal_code", found.postal_code);
      }
    }
  };

  useEffect(() => {
    if (is_head_office) {
      setValue("company.branch_no", "000000");
      trigger("company.branch_no");
    }
  }, [is_head_office]);

  useEffect(() => {
    onCompanyPostalCode();
  }, [company_id_province, company_id_district, company_id_subdistrict]);

  return (
    <div className="company">
      <h2>{msg.company_information}</h2>
      <section>
        <div className="section_company_name">
          <div className="_input_text_select">
            <label>{`${msg.company_name} * (${msg.thai})`}</label>
            <input
              placeholder={msg.company_name}
              {...register("company.company_name_thai", {
                required: true,
              })}
              className={
                errors.company?.company_name_thai ? "_data_invalid" : ""
              }
              disabled={!_temp.isEdit}
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg.company_name} (${msg.english})`}</label>
            <input
              placeholder={msg.company_name}
              {...register("company.company_name_english")}
              disabled={!_temp.isEdit}
            />
          </div>
        </div>
        <div className="_input_text_select">
          <label>{`${msg.address} *`}</label>
          <input
            placeholder={msg.address}
            {...register("company.address", {
              required: true,
            })}
            className={errors.company?.address ? "_data_invalid" : ""}
            disabled={!_temp.isEdit}
          />
        </div>
        <div className="select_address_company_block">
          <div className="_input_text_select">
            <label>{`${msg.branch_no} *`}</label>
            <input
              placeholder={msg.branch_no}
              {...register("company.branch_no", {
                required: true,
              })}
              className={errors.company?.branch_no ? "_data_invalid" : ""}
              disabled={!_temp.isEdit || is_head_office}
            />
            <span className="extra_checkbox">
              <input
                type="checkbox"
                {...register("company.head_office")}
                className={errors.company?.head_office ? "_data_invalid" : ""}
                disabled={!_temp.isEdit}
              />
              <label>{msg.head_office}</label>
            </span>
          </div>
          <div className="_input_text_select">
            <label>{`${msg.country} *`}</label>
            <select
              {...register("company.id_country", {
                required: true,
              })}
              className={errors.company?.id_country ? "_data_invalid" : ""}
              disabled={!_temp.isEdit || true}
            >
              <option value="" label={msg.country} />
              {(countryOption || []).map((el) => (
                <option
                  key={+el.value}
                  value={+el.value || ""}
                  label={language === "en" ? el.label_en : el.label_th}
                />
              ))}
            </select>
          </div>
          <div className="_input_text_select">
            <label>{`${msg.province} *`}</label>
            <select
              {...register("company.id_province", {
                required: true,
                onChange: () => {
                  setValue("company.id_district", "");
                  setValue("company.id_subdistrict", "");
                  setValue("company.postal_code", "");
                },
              })}
              className={errors.company?.id_province ? "_data_invalid" : ""}
              disabled={!_temp.isEdit}
            >
              <option value="" label={msg.province} />
              {(provinceOption || []).map((el) => (
                <option
                  key={+el.value}
                  value={+el.value || ""}
                  label={language === "en" ? el.label_en : el.label_th}
                />
              ))}
            </select>
          </div>
          <div className="_input_text_select">
            <label>{`${msg.district} *`}</label>
            <select
              {...register("company.id_district", {
                required: true,
                onChange: () => {
                  setValue("company.id_subdistrict", "");
                  setValue("company.postal_code", "");
                },
              })}
              className={errors.company?.id_district ? "_data_invalid" : ""}
              disabled={!_temp.isEdit}
            >
              <option value="" label={msg.district} />
              {(
                reduceRepeatElement(allAddress, "id_district")
                  .filter((el) => +el.id_province === +company_id_province)
                  .map((el) => ({
                    value: el.id_district,
                    label_en: el.label_en_district,
                    label_th: el.label_th_district,
                  })) || []
              ).map((el) => (
                <option
                  key={+el.value}
                  value={+el.value}
                  label={language === "en" ? el.label_en : el.label_th}
                />
              ))}
            </select>
          </div>
          <div className="_input_text_select">
            <label>{`${msg.subdistrict} *`}</label>
            <select
              {...register("company.id_subdistrict", {
                required: true,
                onChange: () => {
                  setValue("company.postal_code", "");
                  onCompanyPostalCode();
                },
              })}
              className={errors.company?.id_subdistrict ? "_data_invalid" : ""}
              disabled={!_temp.isEdit}
            >
              <option value="" label={msg.subdistrict} />
              {(
                reduceRepeatElement(
                  allAddress.filter(
                    (el) =>
                      +el.id_province === +company_id_province &&
                      +el.id_district === +company_id_district
                  ),
                  "id_subdistrict"
                ).map((el) => ({
                  value: el.id_subdistrict,
                  label_en: el.label_en_subdistrict,
                  label_th: el.label_th_subdistrict,
                })) || []
              ).map((el) => (
                <option
                  key={+el.value}
                  value={+el.value}
                  label={language === "en" ? el.label_en : el.label_th}
                />
              ))}
            </select>
          </div>
          <div className="_input_text_select">
            <label>{`${msg.postal_code} *`}</label>
            <input
              {...register("company.postal_code", {
                required: true,
              })}
              className={errors.company?.postal_code ? "_data_invalid" : ""}
              placeholder={msg.postal_code}
              disabled={!_temp.isEdit}
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg.phone_number} *`}</label>
            <input
              placeholder={msg.phone_number}
              {...register("company.phone_number", {
                required: true,
              })}
              className={errors.company?.phone_number ? "_data_invalid" : ""}
              disabled={!_temp.isEdit}
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg.fax_number} *`}</label>
            <input
              placeholder={msg.fax_number}
              {...register("company.fax_number", {
                required: true,
              })}
              className={errors.company?.fax_number ? "_data_invalid" : ""}
              disabled={!_temp.isEdit}
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg.tax_id}`}</label>
            <input
              placeholder={msg.tax_id}
              {...register("company.tax_id")}
              disabled={!_temp.isEdit}
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg.email}`}</label>
            <input
              type="email"
              placeholder={msg.email}
              {...register("company.email")}
              disabled={!_temp.isEdit}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const GuestSetting = ({ formHook, onCancel, onSave, onEdit }) => {
  const { msg } = useSelector((state) => state.language);
  const {
    register,
    setValue,
    trigger,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = formHook;

  const _temp = watch("_temp");
  const guest_setting_adult_age = watch("guest_setting.adult_age");

  useEffect(() => {
    if (guest_setting_adult_age) {
      setValue("guest_setting.child_age", guest_setting_adult_age);
      trigger("guest_setting.child_age");
    }
  }, [guest_setting_adult_age]);

  return (
    <div className="guest_setting">
      <div className="guest_block">
        <h2>{msg.guest_setting}</h2>
        <section>
          <div className="_input_text_select">
            <label>{`${msg["adult(s)"]} ${
              msg.age
            } * (${msg.above.toLowerCase()})`}</label>
            <input
              placeholder={msg["adult(s)"]}
              {...register("guest_setting.adult_age", {
                required: true,
              })}
              className={errors.guest_setting?.adult_age ? "_data_invalid" : ""}
              disabled={!_temp.isEdit}
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg["child(s)"]} ${
              msg.age
            } * (${msg.below.toLowerCase()})`}</label>
            <input
              placeholder={msg["child(s)"]}
              {...register("guest_setting.child_age", {
                required: true,
              })}
              className={errors.guest_setting?.child_age ? "_data_invalid" : ""}
              disabled
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg["infant(s)"]} ${
              msg.age
            } * (${msg.below.toLowerCase()})`}</label>
            <input
              placeholder={msg["infant(s)"]}
              {...register("guest_setting.infant_age", {
                required: true,
              })}
              className={
                errors.guest_setting?.infant_age ? "_data_invalid" : ""
              }
              disabled={!_temp.isEdit}
            />
          </div>
        </section>
      </div>
      <div className="license_block">
        <section>
          <div className="_input_text_select">
            <label>{`${msg.tour_operator_license}`}</label>
            <input
              placeholder={msg.tour_operator_license}
              {...register("company.tour_operator_license")}
              disabled={!_temp.isEdit}
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg.insurance_policy_number}`}</label>
            <input
              placeholder={msg.insurance_policy_number}
              {...register("company.insurance_policy_number")}
              disabled={!_temp.isEdit}
            />
          </div>
          <div className="_input_text_select">
            <label>{`${msg.insurance_company}`}</label>
            <input
              placeholder={msg.insurance_company}
              {...register("company.insurance_company")}
              disabled={!_temp.isEdit}
            />
          </div>
          <div>
            <button
              hidden={_temp.isEdit}
              onClick={onEdit}
              className="_fill_button"
            >
              {msg.edit}
            </button>
            <button
              hidden={!_temp.isEdit}
              onClick={onCancel}
              className="_outline_button"
            >
              {msg.cancel}
            </button>
            <button
              hidden={!_temp.isEdit}
              onClick={handleSubmit(onSave)}
              className="_fill_button"
            >
              {msg.save}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
